import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useWindowScroll, useWindowSize } from 'react-use';
import { BREAKPOINT_MIN_WIDTH } from '~go/web/src/js/constants/bs5_constants';
import Card from '../../components/common/card/card';
import { Button } from '../../components/common/button/button';
import { clsx } from '../../utils/clsx/clsx';

function ExternalAppButton({ product }) {
  const [show, setShow] = useState(false);
  const { width } = useWindowSize();
  const scrollPosition = useWindowScroll();
  const targetRef = useRef(null);

  const showOverlay = () => {
    if (width >= BREAKPOINT_MIN_WIDTH.md) {
      setShow(true);
    }
  };

  const hideOverlay = () => {
    setShow(false);
  };

  useEffect(() => {
    if (scrollPosition.y > 0 && show) {
      setShow(false);
    }
  }, [scrollPosition.y]);

  return (
    <div
      onMouseEnter={showOverlay}
      onFocus={showOverlay}
      onMouseLeave={hideOverlay}
      onBlur={hideOverlay}
      className="position-relative"
    >
      <a
        key={`${product.name}`}
        href={product.url}
        ref={targetRef}
        className={`d-block text-center px-md-2 py-1 ${product.active && 'bg-white'}`}
        style={{ width: '124px' }}
      >
        <img
          src={product.logo}
          alt={`${product.name}`}
          height="16px"
          className={!product.active ? 'grayscale' : undefined}
        />
      </a>

      {show && (
        <div className="position-absolute zindex-popover-1" style={{ top: 0, left: 0 }}>
          <div className="d-flex">
            <a
              key={`${product.name}`}
              href={product.url}
              ref={targetRef}
              className="d-block text-center px-md-2 py-1 border-right border-left border-light-gray zindex-1 position-relative"
              style={{
                width: '124px',
                background: 'var(--white)',
                boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.15)',
                clipPath: 'inset(-10px -10px 0px -10px)',
              }}
            >
              <img src={product.logo} alt={`${product.name}`} height="16px" />
            </a>
            <div className="flex-grow-1" onMouseEnter={hideOverlay} onFocus={hideOverlay} />
          </div>
          <Card
            borderRadius={false}
            className="overflow-auto"
            style={{
              marginTop: -1,
              borderRadius: '0 16px 16px 16px',
              boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.15)',
              maxHeight: 'calc(100vh - 40px)',
            }}
          >
            <Card.Body>
              <div className="d-flex flex-column gap-3">
                <img src={product.logo} alt={`${product.name}`} height="32px" />
                <div className={clsx('rounded-lg', product.information.imgContainerClasses)}>
                  <img alt={`${product.name} cta`} src={product.information.img} width={260} />
                </div>
                <div>
                  <h4>{product.information.header}</h4>
                  <h5 className="font-weight-normal text-tertiary-text">{product.information.description}</h5>
                </div>
                <div className="d-none d-lg-flex flex-column gap-1 w-100 align-items-start">
                  {product.information.features.map(feature => (
                    <div
                      key={feature}
                      style={{ borderRadius: 12, padding: '6px 12px' }}
                      className="border border-grey text-default-text font-weight-normal"
                    >
                      {feature}
                    </div>
                  ))}
                </div>

                <Button size="large" component="a" href={product.url}>
                  Learn more about {product.name}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
}

ExternalAppButton.propTypes = {
  product: PropTypes.shape({
    url: PropTypes.string,
    logo: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    width: PropTypes.string,
    information: PropTypes.shape({
      imgContainerClasses: PropTypes.string,
      img: PropTypes.string,
      header: PropTypes.string,
      description: PropTypes.string,
      features: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  }).isRequired,
};

export default ExternalAppButton;
